import React, { useState, useContext, useRef, useEffect } from "react";
import clsx from "clsx";

import { useForm, FormProvider, Controller } from "react-hook-form";

import {
  FullScreenModal,
  FullScreenModalBody,
  FullScreenModalHeader,
  FullScreenModalFooter,
  Notification,
  NotificationType,
  Loader,
} from "@transfr-inc/dashboard-components";
import {
  Button,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "@transfr-inc/dashboard-components/forms";
import { services } from "../../../dependencies";
import { SessionContext } from "../../../context";
import "./manage-activity.modal.scss";

import { AllItemTypes } from "../constants/item-types.model";
import { ActivityForm } from "@components/forms/activity-template";

export const ManageActivityModal = ({
  setShow,
  show,
  className,
  onActivitySaved,
  activity = null,
  mode = "add",
}) => {
  const isEditMode = mode == "edit" || !!activity;
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState();
  const [defaultValues, setDefaultValues] = useState(
    activity || { itemType: AllItemTypes[0] }
  );
  const originalDataRef = useRef(activity);
  const { currentUser } = useContext(SessionContext);
  const { libraryService } = services;

  const activityMethods = useForm({
    criteriaMode: "all",
    mode: "onChange",
    defaultValues: activity || undefined,
    shouldUnregister: true,
  });

  const { register, handleSubmit, control, formState, trigger, reset } =
    activityMethods;
  const { isValid } = formState;

  const onClose = () => {
    setShow(false);
  };

  const initModal = () => {
    originalDataRef.current = activity;
    const initialValues = activity || { itemType: AllItemTypes[0] };
    reset(initialValues);
    setDefaultValues(initialValues);
  };

  async function submitAddform(values) {
    setLoading(true);

    try {
      const activityData = {
        title: values.title,
        description: values.description,
        contentType: values.contentType,
        fileName: values.uploadZip.name,
        file: values.uploadZip,
      };

      const response = await libraryService.addActivity(activityData);

      onActivitySaved(activityData, response);
      onClose();
    } catch (error) {
      setLoading(false);
      setNotification({
        type: NotificationType.error,
        message: "Failed to save activity. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }

  async function submitEditform(values) {
    setLoading(true);
    try {
      const original = originalDataRef.current || activity;

      const newContentType = values.activityType?.id || values.contentType;
      const originalContentType = original?.contentType;

      const activityData = {};

      if (values.title !== original?.title) {
        activityData.title = values.title;
      }

      if (values.description !== (original?.description || "")) {
        activityData.description = values.description;
      }

      if (newContentType !== originalContentType) {
        activityData.contentType = newContentType;
      }

      const uploadZip = values.uploadZip;
      const isNewFile = uploadZip instanceof File && uploadZip.size > 0;

      if (isNewFile) {
        activityData.newFile = true;
        activityData.file = uploadZip;
      }

      if (Object.keys(activityData).length === 0) {
        setLoading(false);
        onClose();
        return;
      }

      const response = await libraryService.updateActivityInLibrary(
        activity.id,
        activityData
      );

      onActivitySaved(activityData, response, activity.id);
      onClose();
    } catch (error) {
      setLoading(false);
      setNotification({
        type: NotificationType.error,
        message:
          error?.message ||
          error?.detail ||
          "Failed to save activity. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }

  const onClick = async () => {
    if (isEditMode) {
      handleSubmit(submitEditform)();
    } else {
      handleSubmit(submitAddform)();
    }
  };

  useEffect(() => {
    if (show) {
      initModal();
    }
  }, [show, activity]);

  useEffect(() => {
    show && trigger();
  }, [show]);

  return (
    <div className={clsx("manage-activity-modal", className)}>
      <FullScreenModal onClose={onClose} open={show} preventClose>
        <FullScreenModalHeader
          title={isEditMode ? "Edit Activity" : "Add Activity"}
          titleIcon={
            isEditMode ? "fa-regular fa-pen-to-square" : "fa-regular fa-plus"
          }
        />
        <FullScreenModalBody>
          <div className={"manage-activity-modal-body"}>
            {loading && <Loader overlay></Loader>}
            <div className="text-with-info">
              <label>
                Required Fields<sup>*</sup>
              </label>
            </div>

            <div className="template-info">
              {notification?.type == NotificationType.error && (
                <div className="error">
                  <Notification
                    {...notification}
                    closable
                    animated
                    onClose={() => setNotification()}
                  >
                    {notification.message}
                  </Notification>
                </div>
              )}
            </div>

            <FormProvider {...activityMethods}>
              <form className={clsx("item-form", className)} noValidate={true}>
                <Controller
                  control={control}
                  name="itemType"
                  defaultValue={defaultValues?.itemType || AllItemTypes[0]}
                  rules={{
                    required: {
                      value: true,
                    },
                  }}
                  render={({ field: { onBlur, onChange, value, ref } }) => {
                    const selectedItemType = value || AllItemTypes[0];
                    return (
                      <div className="item-type">
                        <div className="section-title">
                          <h3>
                            Type <sup>*</sup>
                          </h3>
                        </div>
                        <Dropdown
                          selected={selectedItemType?.id}
                          onOptionSelected={(option) => onChange(option)}
                        >
                          <DropdownButton
                            placeholder={"Select an item type..."}
                          >
                            {selectedItemType?.displayName}
                          </DropdownButton>
                          <DropdownItems>
                            {AllItemTypes.map((type) => (
                              <DropdownItem key={type.id} value={type}>
                                {type.displayName}
                              </DropdownItem>
                            ))}
                          </DropdownItems>
                        </Dropdown>
                      </div>
                    );
                  }}
                />
                <ActivityForm defaultValues={defaultValues} />
              </form>
            </FormProvider>
          </div>
        </FullScreenModalBody>
        <FullScreenModalFooter>
          <Button disabled={loading} onClick={onClose}>
            Cancel
          </Button>
          <Button primary disabled={!isValid || loading} onClick={onClick}>
            Save
          </Button>
        </FullScreenModalFooter>
      </FullScreenModal>
    </div>
  );
};

export default ManageActivityModal;
